import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import PinInput from "./PinInput";
import { GoogleButton } from "./GoogleButton";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { rankOptions } from "../mock";
import { Anchor, Ship, GraduationCap, User, Loader2, Briefcase } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export const ROLES = [
  { id: "seafarer", label: "Seafarer", desc: "Find jobs & join the crew", icon: Anchor },
  { id: "company", label: "Shipping Company", desc: "Hire verified crew", icon: Ship },
  { id: "institute", label: "Maritime Institute", desc: "Courses & admissions", icon: GraduationCap },
];

export const RolePicker = ({ value, onChange }) => (
  <div className="grid w-full grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3" role="radiogroup" aria-label="Select your role">
    {ROLES.map((r) => {
      const Icon = r.icon;
      const active = value === r.id;
      return (
        <button
          key={r.id}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(r.id)}
          data-testid={`role-option-${r.id}`}
          className={`group relative flex items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left transition-colors duration-200 sm:flex-col sm:items-center sm:gap-2 sm:px-3 sm:py-4 sm:text-center ${
            active
              ? "border-[#FF4500] bg-orange-50/70"
              : "border-gray-200 bg-white hover:border-[#FF4500]/40 hover:bg-orange-50/30"
          }`}
        >
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${active ? "bg-[#FF4500] text-white" : "bg-orange-50 text-[#FF4500]"}`}>
            <Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1 sm:flex-none">
            <span className={`block text-sm font-bold ${active ? "text-gray-900" : "text-gray-700"}`}>
              {r.id === "seafarer" ? "Seafarer" : r.id === "company" ? "Company" : "Institute"}
            </span>
            <span className="mt-0.5 block text-xs leading-snug text-gray-500 sm:hidden">{r.desc}</span>
          </span>
          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors sm:absolute sm:right-2.5 sm:top-2.5 sm:h-4 sm:w-4 ${active ? "border-[#FF4500] bg-[#FF4500]" : "border-gray-300 bg-white"}`}>
            {active && <span className="h-2 w-2 rounded-full bg-white sm:h-1.5 sm:w-1.5" />}
          </span>
        </button>
      );
    })}
  </div>
);

const Signup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { register, user, loading } = useAuth();

  const [role, setRole] = useState(() => {
    const r = new URLSearchParams(window.location.search).get("role");
    return ["seafarer", "company", "institute"].includes(r) ? r : "seafarer";
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rank, setRank] = useState("");
  const [organization, setOrganization] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/feed");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(password)) {
      toast({ title: "PIN must be 6 digits", description: "Choose a 6-digit number you'll remember." });
      return;
    }
    setSubmitting(true);
    try {
      await register({ name, email, password, role, rank, organization, job_title: jobTitle });
      toast({ title: "Welcome aboard!", description: "Your account is ready." });
      navigate("/feed");
    } catch (err) {
      toast({
        title: "Sign up failed",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSeafarer = role === "seafarer";
  const orgLabel = role === "institute" ? "Institute name" : "Company name";

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Join seafarers, companies and institutes on Indosbook."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-[#FF4500] hover:underline">
            Log in
          </Link>
        </>
      }
    >
      <GoogleButton label="Sign up with Google" />

      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-sm text-gray-400">or</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-1.5">
          <Label>I am joining as</Label>
          <RolePicker value={role} onChange={setRole} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="name">{isSeafarer ? "Full name" : "Contact name"}</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-12 pl-10" />
          </div>
        </div>

        {isSeafarer ? (
          <div className="space-y-1.5">
            <Label>Rank</Label>
            <Select value={rank} onValueChange={setRank}>
              <SelectTrigger className="h-12"><SelectValue placeholder="Select your rank" /></SelectTrigger>
              <SelectContent className="max-h-56">
                {rankOptions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="org">{orgLabel}</Label>
              <div className="relative">
                <Ship className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input id="org" required value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder={orgLabel} className="h-12 pl-10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="job-title">Your Job Title / Role</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input id="job-title" required value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} data-testid="signup-job-title"
                  placeholder={role === "institute" ? "e.g. Admissions Head, Principal" : "e.g. Crewing Manager, HR Executive"} className="h-12 pl-10" />
              </div>
            </div>
          </>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Create a 6-digit PIN</Label>
          <PinInput id="password" required value={password} onChange={setPassword} placeholder="••••••" data-testid="signup-pin" />
          <p className="text-xs text-gray-400">You'll use this PIN with your email to log in.</p>
        </div>

        <Button type="submit" disabled={submitting} data-testid="signup-submit" className="h-12 w-full rounded-full bg-[#FF4500] text-base font-bold text-white hover:bg-[#e03d00]">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Account"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Signup;
